import { MaterialCommunityIcons } from '@expo/vector-icons';
import { Pressable, StyleSheet, View } from 'react-native';
import { Text } from 'react-native-paper';

import { CountBadge } from '@/components/atoms/badge';
import { iconSize, rippleOnDark, unisonColors } from '@/theme';

const BAR_HEIGHT = 60;
const RIPPLE_RADIUS = 32;

export interface BottomTabItem {
  key: string;
  label: string;
  iconName: string;
  isActive: boolean;
  isAction?: boolean;
  badgeCount?: number;
  onPress: () => void;
}

interface BottomTabBarProps {
  items: BottomTabItem[];
  bottomInset?: number;
}

interface TabItemProps {
  item: BottomTabItem;
}

const TabItem = ({ item }: TabItemProps) => (
  <Pressable
    style={({ pressed }) => [styles.item, pressed && styles.itemPressed]}
    onPress={item.onPress}
    android_ripple={{ color: rippleOnDark, borderless: true, radius: RIPPLE_RADIUS }}
    accessibilityRole={item.isAction ? 'button' : 'tab'}
    accessibilityState={item.isAction ? undefined : { selected: item.isActive }}
    accessibilityLabel={item.label}>
    <View style={styles.itemContent}>
      <View style={styles.iconContainer}>
        <MaterialCommunityIcons
          name={item.iconName as keyof typeof MaterialCommunityIcons.glyphMap}
          size={iconSize.md}
          color={item.isActive ? unisonColors.brandGreenStrong : unisonColors.borderNdBase}
        />
        {item.badgeCount != null && (
          <View style={styles.badge}>
            <CountBadge count={item.badgeCount} testID={`tab-badge-${item.key}`} />
          </View>
        )}
      </View>
      <Text style={[styles.label, item.isActive && styles.labelActive]}>{item.label}</Text>
    </View>
  </Pressable>
);

export const BottomTabBar = ({ items, bottomInset = 0 }: BottomTabBarProps) => (
  <View
    style={[
      styles.container,
      {
        height: BAR_HEIGHT + bottomInset,
        paddingBottom: bottomInset,
      },
    ]}>
    {items.map((item) => (
      <TabItem key={item.key} item={item} />
    ))}
  </View>
);

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    height: BAR_HEIGHT,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: unisonColors.bgNdStrong,
    overflow: 'hidden',
  },
  item: {
    flex: 1,
    height: '100%',
  },
  itemPressed: {
    opacity: 0.55,
  },
  itemContent: {
    flexDirection: 'column',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'center',
    paddingTop: 6,
    paddingBottom: 6,
  },
  iconContainer: {
    width: 56,
    height: 32,
    alignItems: 'center',
    justifyContent: 'center',
  },
  badge: {
    position: 'absolute',
    top: 0,
    right: 8,
  },
  label: {
    fontSize: 11,
    lineHeight: 16,
    letterSpacing: 0.5,
    fontWeight: '500',
    textAlign: 'center',
    color: unisonColors.borderNdBase,
  },
  labelActive: {
    color: unisonColors.brandGreenStrong,
  },
});
